import React, { useContext, useMemo } from "react";
import {
    Box,
    Checkbox,
    FormGroup,
    FormControlLabel,
    Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AppContext } from "../../App";
import { selectChartConfig } from "../../selectors/chart";
import { VolumeCandleSeriesOptions } from "../chartV2/customSeries/volumeCandleSeries/options";
import ColorSelector from "../colorSelector/colorSelector";
import tinycolor from "tinycolor2";
import CommonChartSettingsTabs from './commonChartSettingsTabs';
import { chartSettingsFormGroupSx, chartSettingsColorRowSx, chartSettingsRowSx, chartSettingsSectionTitleSx, chartSettingsCheckboxColorLabelSx } from './chartSettingsLayout';

type Props = {
    viewKey: string;
};

const VolumeCandlesSettings = ({ viewKey }: Props) => {
    const appContext = useContext(AppContext)
    const chart = appContext.getChart(viewKey)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const primarySeriesGroupId = useMemo(() => {
        return chart?.primarySeriesGroup?.config?.id
    }, [chart?.primarySeriesGroup?.config?.id])

    const upColor = useMemo(() => {
        let color = '#26a69a';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.upColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const downColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.downColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const borderUpColor = useMemo(() => {
        let color = '#26a69a';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.borderUpColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const borderDownColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.borderDownColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const wickUpColor = useMemo(() => {
        let color = '#26a69a';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.wickUpColor ?? '#26a69a'
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const wickDownColor = useMemo(() => {
        let color = '#ef5350';
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            color = (series as any)?.wickDownColor ?? '#ef5350'
        }
        return tinycolor(color)
    }, [chartConfig, primarySeriesGroupId])

    const visible = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.visible ?? true
        }
        return true
    }, [chartConfig, primarySeriesGroupId])

    const borderVisible = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.borderVisible ?? false
        }
        return false
    }, [chartConfig, primarySeriesGroupId])

    const wickVisible = useMemo(() => {
        if (primarySeriesGroupId) {
            const seriesId = chartConfig?.seriesGroups?.[primarySeriesGroupId]?.series?.[0]
            const series = chartConfig.series?.[seriesId ?? ''].options
            return (series as any)?.wickVisible ?? true
        }
        return true
    }, [chartConfig, primarySeriesGroupId])

    const handleColorChangeSeries = (newColor: string, type: keyof VolumeCandleSeriesOptions) => {
        if (chart) {
            (chart.primarySeriesGroup?.getPrimarySeries() as any)?.updateOptions({
                [type]: newColor
            });
        }
    }

    const handleBooleanChange = (value: boolean, type: keyof VolumeCandleSeriesOptions) => {
        if (chart) {
            (chart.primarySeriesGroup?.getPrimarySeries() as any)?.updateOptions({
                [type]: value
            });
        }
    }

    const symbolTabContent = (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                VOLUME CANDLES
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <Box sx={chartSettingsColorRowSx}>
                    <FormControlLabel sx={chartSettingsCheckboxColorLabelSx} control={
                        <Checkbox
                            checked={visible}
                            onChange={(e) => handleBooleanChange(e.target.checked, "visible")}
                        />} label="Body"
                    />
                    <ColorSelector compact showOnlySwatch color={upColor.toHexString()} opacity={upColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'upColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={downColor.toHexString()} opacity={downColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'downColor');
                        }} />
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <FormControlLabel sx={chartSettingsCheckboxColorLabelSx} control={
                        <Checkbox
                            checked={borderVisible}
                            onChange={(e) => handleBooleanChange(e.target.checked, "borderVisible")}
                        />} label="Borders"
                    />
                    <ColorSelector compact showOnlySwatch color={borderUpColor.toHexString()} opacity={borderUpColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'borderUpColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={borderDownColor.toHexString()} opacity={borderDownColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'borderDownColor');
                        }} />
                </Box>
                <Box sx={chartSettingsColorRowSx}>
                    <FormControlLabel sx={chartSettingsCheckboxColorLabelSx} control={
                        <Checkbox
                            checked={wickVisible}
                            onChange={(e) => handleBooleanChange(e.target.checked, "wickVisible")}
                        />} label="Wick"
                    />
                    <ColorSelector compact showOnlySwatch color={wickUpColor.toHexString()} opacity={wickUpColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'wickUpColor');
                        }} />
                    <ColorSelector compact showOnlySwatch color={wickDownColor.toHexString()} opacity={wickDownColor.getAlpha()}
                        onColorChange={(color, transparency) => {
                            const newColor = tinycolor(color).setAlpha(transparency).toHex8String();
                            handleColorChangeSeries(newColor, 'wickDownColor');
                        }} />
                </Box>
            </FormGroup>
        </Box>
    )

    return <CommonChartSettingsTabs viewKey={viewKey} symbolTabContent={symbolTabContent} />
}

export default VolumeCandlesSettings;
